import { useState, useRef, useEffect, useCallback } from 'react';
import { requestPlan as requestPlanApi } from '../services/generatorBatchApi';

/**
 * Richiede il piano di generazione senza avviare processi.
 * Gestisce sia i `blockingErrors` restituiti in un 200 sia l'errore controllato
 * `422 INVALID_GENERATION_PLAN` con array `details`.
 */
export function usePlan() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // stringa messaggio
  const [blockingErrors, setBlockingErrors] = useState([]);
  const abortRef = useRef(null);

  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
    },
    [],
  );

  const build = useCallback((body) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    setBlockingErrors([]);

    return requestPlanApi(body, controller.signal)
      .then((data) => {
        setPlan(data);
        setBlockingErrors(Array.isArray(data?.blockingErrors) ? data.blockingErrors : []);
        return data;
      })
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return null;
        const resp = err?.response?.data;
        if (resp?.error === 'INVALID_GENERATION_PLAN' && Array.isArray(resp.details)) {
          setBlockingErrors(resp.details);
          setError(resp.message || 'Il piano contiene errori bloccanti.');
        } else {
          setError(resp?.message || 'Impossibile calcolare il piano.');
        }
        setPlan(null);
        return null;
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  }, []);

  const reset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setPlan(null);
    setError(null);
    setBlockingErrors([]);
    setLoading(false);
  }, []);

  return { plan, loading, error, blockingErrors, build, reset };
}
